import { useState, useRef, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { 
  Bot, 
  Send, 
  Loader2, 
  X, 
  Sparkles,
  MessageSquare,
  TrendingUp,
  Zap,
  BarChart3
} from 'lucide-react';
import { cn } from '../lib/utils';

const SUGGESTED_QUERIES = [
  { icon: TrendingUp, text: "Which ideas have the most benefit?", color: "text-emerald-600" },
  { icon: Zap, text: "Which ideas are easiest to implement?", color: "text-amber-600" },
  { icon: BarChart3, text: "Rank all ideas for me", color: "text-blue-600" },
  { icon: Sparkles, text: "What are the Quick Wins?", color: "text-purple-600" },
];

const AIAssistant = ({ onClose }) => {
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    // Add welcome message
    setMessages([{
      role: 'assistant',
      content: "Hi! I'm your CI Assistant. I can help you analyze ideas and projects. Ask me questions like:\n\n• Which ideas have the most benefit?\n• Which are easiest to implement?\n• Rank the ideas for me\n• What are the Quick Wins?\n• Compare ROI across projects"
    }]);
  }, []);

  useEffect(() => {
    // Scroll to bottom when messages change
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async (customQuery = null) => {
    const queryToSend = customQuery || query;
    if (!queryToSend.trim() || loading) return;

    const userMessage = { role: 'user', content: queryToSend };
    setMessages(prev => [...prev, userMessage]);
    setQuery('');
    setLoading(true);

    try {
      const response = await api.post('/ai/analyze-ideas', {
        query: queryToSend,
        session_id: sessionId
      });

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response
      }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "I'm sorry, I encountered an error processing your request. Please try again.",
        error: true
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedQuery = (text) => {
    setQuery(text);
    handleSend(text);
  };

  return (
    <Card className="fixed bottom-36 right-6 w-96 h-[500px] shadow-2xl z-50 flex flex-col border-2 border-[#235894] animate-slide-in" data-testid="ai-assistant-panel">
      {/* Header */}
      <CardHeader className="bg-gradient-to-r from-[#235894] to-[#26B8A7] text-white rounded-t-lg py-3 px-4 flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <Bot className="w-5 h-5" />
          </div>
          <div>
            <CardTitle className="text-sm font-semibold">CI Assistant</CardTitle>
            <p className="text-xs text-white/80">Powered by AI</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={onClose}
          className="text-white hover:bg-white/20 h-8 w-8"
          data-testid="close-ai-assistant"
        >
          <X className="w-4 h-4" />
        </Button>
      </CardHeader>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-4">
          {messages.map((msg, index) => (
            <div
              key={index}
              className={cn(
                "flex",
                msg.role === 'user' ? "justify-end" : "justify-start"
              )}
            >
              <div
                className={cn(
                  "max-w-[85%] rounded-lg px-3 py-2 text-sm",
                  msg.role === 'user'
                    ? "bg-[#235894] text-white"
                    : msg.error
                    ? "bg-red-50 text-red-700 border border-red-200"
                    : "bg-slate-100 text-slate-700"
                )}
              >
                <div className="whitespace-pre-wrap">{msg.content}</div>
              </div>
            </div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="bg-slate-100 rounded-lg px-3 py-2 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-[#235894]" />
                <span className="text-sm text-slate-500">Analyzing...</span>
              </div>
            </div>
          )}
        </div>

        {/* Suggested queries - show only if no user messages yet */}
        {messages.length === 1 && (
          <div className="mt-4 space-y-2">
            <p className="text-xs text-slate-400 uppercase tracking-wider">Try asking:</p>
            {SUGGESTED_QUERIES.map((sq, index) => (
              <button
                key={index}
                onClick={() => handleSuggestedQuery(sq.text)}
                className="w-full flex items-center gap-2 p-2 rounded-lg border border-slate-200 hover:bg-slate-50 hover:border-[#235894] transition-colors text-left"
                data-testid={`suggested-query-${index}`}
              >
                <sq.icon className={cn("w-4 h-4", sq.color)} />
                <span className="text-sm text-slate-700">{sq.text}</span>
              </button>
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <div className="p-3 border-t border-slate-200">
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about your ideas..."
            className="flex-1"
            disabled={loading}
            data-testid="ai-query-input"
          />
          <Button
            onClick={() => handleSend()}
            disabled={!query.trim() || loading}
            className="bg-[#235894] hover:bg-[#1E4B80]"
            size="icon"
            data-testid="send-ai-query-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default AIAssistant;
